import React, { useState } from 'react';
import { Box, Typography, FormControl, Divider } from '@mui/material';
import { getApolloDiseaseDetection, setApolloDiseaseDetectionGapStatus, setApolloDiseaseDetectionRecStatus, getApolloAllymarPDF } from '../../api';
import useFetch from '../../hooks/useFetch';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import Accordion from '@mui/material/Accordion';
import AccordionSummary from '@mui/material/AccordionSummary';
import AccordionDetails from '@mui/material/AccordionDetails';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import Checkbox from '@mui/material/Checkbox';
import Button from '@mui/material/Button';
import Feedback from './Feedback';
import Tooltip from '@mui/material/Tooltip';
import { makeStyles } from '@mui/styles';
import { Dialog, DialogContent, DialogTitle, DialogActions, TableHead } from '@mui/material';


const TealDiseaseDetection = ({ patientId, username, token }) => {
    const [reload, setReload] = useState(true);
    const { data: patient, loading, error } = useFetch(getApolloDiseaseDetection, patientId, { username, token, reload });
    const [hccGaps, setHccGaps] = React.useState([]);
    const [hccCoding, setHccCoding] = React.useState([]);
    const [hccRecaptures, setHccRecaptures] = React.useState([]);
    const [hccRecommendations, setHccRecommendations] = React.useState([]);
    const [isFeedbackOpen, setIsFeedbackOpen] = useState(false);
    const [feedbackConfig, setFeedbackConfig] = useState({});
    const [openNotesModal, setOpenNotesModal] = useState(false);  
    const [activeFeedbacks, setActiveFeedbacks] = useState([]);
    const [openEvidenceModal, setOpenEvidenceModal] = useState(false);
    const [activeEvidences, setActiveEvidences] = useState([]);
    const [activeDiagnosisHeader, setActiveDiagnosisHeader] = useState('');
    const [expandedAllymarNotes, setExpandedAllymarNotes] = useState({});
    const [loadingFile, setLoadingFile] = useState(false);

  
    const useStyles = makeStyles({
        tooltip: {
          fontSize: "1em",
        },
        hoverableText: {
          cursor: 'pointer', // Makes it clear the text is interactive
          textDecoration: 'underline', // Adds an underline for hoverable text
        },
    });
    const classes = useStyles();

    const handleCloseFeedback = () => {
      setIsFeedbackOpen(false);
      setReload(!reload);
    };

    const handleViewNotes = (feedbacks) => {
      setActiveFeedbacks(feedbacks);
      setOpenNotesModal(true);
    };
    
    const handleCloseNotesModal = () => {
        setOpenNotesModal(false);
        setActiveFeedbacks([]);
    };

    const handleViewEvidenceModal = (evidences, headerLabel) => {
      setActiveEvidences(evidences);
      setActiveDiagnosisHeader(headerLabel);
      setOpenEvidenceModal(true);
    };

    const handleCloseEvidenceModal = () => {
      setOpenEvidenceModal(false);
      setActiveEvidences([]);
      setActiveDiagnosisHeader('');
    };

    const handleOpenPdf = async (filename) => {
        setLoadingFile(true);
        try {
          const response = await getApolloAllymarPDF({
            username,
            token,
            filename
          });
      
          const blob = new Blob([response.data], { type: 'application/pdf' });
          const url = URL.createObjectURL(blob);
      
          // Open in new tab/window
          window.open(url, '_blank', 'noopener,noreferrer');
      
          // Optional: revoke the object URL after a short delay to free memory
          setTimeout(() => {
            URL.revokeObjectURL(url);
          }, 10000); // after 10 seconds
      
        } catch (error) {
          console.error('Error fetching PDF:', error);
          alert('Failed to fetch PDF');
        } finally {
          setLoadingFile(false);
        }
      };

    React.useEffect(() => {
        if (patient && patient.HccGaps) {
            setHccGaps(patient.HccGaps);
        }
        if (patient && patient.HccRecommendations) {
            setHccRecommendations(patient.HccRecommendations);
        }
        if (patient && patient.Coding) {
          setHccCoding(patient.Coding);
        }
        if (patient && patient.HccRecaptures) {
          setHccRecaptures(patient.HccRecaptures);
        }
    }, [patient,reload]);

    if (loading) return <Typography>Loading...</Typography>;
    if (error) return <Typography>Error: {error.message}</Typography>;

    const handleAckGaps = (event, diagnosisCode) => {
        setApolloDiseaseDetectionGapStatus(patientId, { username, token}, diagnosisCode, 'ACK');
        setHccGaps(prevState => prevState.map(gap =>
            gap.DiagnosisCode === diagnosisCode ? { ...gap, Status: 'ACK' } : gap
        ));
    };

    const handleChangeGaps = (event, diagnosisCode) => {
        const { value } = event.target;
        setApolloDiseaseDetectionGapStatus(patientId, { username, token}, diagnosisCode, value);
        setHccGaps(prevState => prevState.map(gap =>
            gap.DiagnosisCode === diagnosisCode ? { ...gap, Status: value } : gap
        ));
    };

    const handleAckRecapture = (event, messageId, moduleName) => {
      setFeedbackConfig({
        title: 'Resolve Recapture',
        feedback_description: 'Additional Information',
        module_category: 'DiseaseDetection',
        module_name: moduleName,
        module_item_key: messageId,
      });
      setIsFeedbackOpen(true);
    };

    const handleAckCoding = (event, messageId, moduleName) => {
      setFeedbackConfig({
        title: 'Resolve Coder Suggestion',
        feedback_description: 'Additional Information',
        module_category: 'DiseaseDetection',
        module_name: moduleName,
        module_item_key: messageId,
      });
      setIsFeedbackOpen(true);
    };

    const handleAckRecommendations = (event, diagnosisCode) => {
      setApolloDiseaseDetectionRecStatus(patientId, { username, token}, diagnosisCode, 'ACK');
      setHccRecommendations(prevState => prevState.map(rec =>
        rec.DiagnosisCode === diagnosisCode ? { ...rec, Status: 'ACK' } : rec
      ));
    };

    const handleChangeRecommendations = (event, diagnosisCode) => {
        const { value } = event.target;
        setApolloDiseaseDetectionRecStatus(patientId, { username, token}, diagnosisCode, value);
        setHccRecommendations(prevState => prevState.map(rec =>
            rec.DiagnosisCode === diagnosisCode ? { ...rec, Status: value } : rec
        ));
    };

    const handleToggleAllymarNote = (messageId) => {
      setExpandedAllymarNotes(prev => ({
        ...prev,
        [messageId]: !prev[messageId]
      }));
    };

    if (patient.ActiveGap === 0 && patient.ActiveRec === 0 && patient.ActiveCoding == 0 && patient.ActiveRecap === 0 && patient.ActiveAllymar === 0) {
      return (<div></div>)
    }

    let gapComponent = ""
    let recCompoent = ""
    let codingComponent = ""
    let allymarComponent = "";
    let hasInfo = 0
    if (patient.ActiveGap === 1 || patient.ActiveRecap === 1) {
        gapComponent = (<div><Box
            display="flex"
            alignItems="center"
            justifyContent="space-between"
            bgcolor="teal"
            color="white"
            p={1}
            borderRadius="4px 4px 0 0"
        >
            HCC Gaps
        </Box>
        <TableContainer component={Paper}>
            <Table aria-label="simple table">
                <TableBody>
                    {hccGaps.map(gap => (
                        <TableRow key={gap.MessageId} sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                            <TableCell sx={{ padding: '4px', bgcolor: "#ff9d9c"  }} component="th" scope="row">
                            <Tooltip title={gap.DiagnosisDescription} arrow classes={{tooltip: classes.tooltip}}><span className={classes.hoverableText}>{gap.DiagnosisCode}</span></Tooltip> (HCC {gap.HCC}): {gap.Description}<br /><span style={{ fontSize: '0.8em', color: '#555555' }}>Last Assessed: {gap.LastAssessedDate}</span>
                            </TableCell>
                            <TableCell sx={{ padding: '4px', bgcolor: "#ff9d9c"  }} align="right">
                                <FormControl variant="standard" sx={{ m: 0, minWidth: 46 }}>
                                  <Checkbox
                                    sx={{ '& .MuiSvgIcon-root': { fontSize: 28 } }}
                                    onChange={(event) => handleAckGaps(event, gap.MessageId)}
                                  />
                                </FormControl>
                            </TableCell>
                        </TableRow>
                    ))}

                    {hccRecaptures.map((recaps, i) => (
                      <React.Fragment key={i}>
                      {
                       recaps.map((recap) => { 
                          
                          if (recap.ModuleName === "HccRecapture") {
                            const latestEvidence = recap.Evidences
                              .map(e => new Date(e.detected_date))
                              .sort((a, b) => b - a)[0]?.toISOString().split('T')[0];
                            return (
                              <React.Fragment key={recap.MessageId}>
                                {/* Spacer Row */}
                                <TableRow>
                                  <TableCell colSpan={2} sx={{ padding: '3px', bgcolor: 'darkgray' }} />
                                </TableRow>

                                {/* Header Row */}
                                <TableRow>
                                  <TableCell colSpan={2} sx={{ padding: '8px', bgcolor: 'lightgray' }}>
                                    <Typography component="span" sx={{ fontWeight: 'bold' }}>
                                      HCC {recap.HCC} - {recap.HCCDescription}
                                    </Typography>
                                    <br />
                                    <span style={{ fontSize: '0.8em', color: '#555555' }}>
                                      Last Assessed: {latestEvidence || 'N/A'}
                                    </span>
                                  </TableCell>
                                </TableRow>

                                {/* Action Row with View + Resolve buttons side-by-side */}
                                <TableRow>
                                  <TableCell colSpan={2} align="right" sx={{ padding: '6px', bgcolor: '#fdedec' }}>
                                    <Box display="flex" justifyContent="flex-end" gap={1}>
                                      <Button
                                        variant="contained"
                                        size="small"
                                        sx={{
                                          backgroundColor: '#ADD8E6',
                                          color: '#000',
                                          fontSize: '12px',
                                          padding: '4px 8px',
                                          '&:hover': { backgroundColor: '#BFEFFF' },
                                        }}
                                        onClick={() =>
                                          handleViewEvidenceModal(recap.Evidences, `${recap.MessageId} - ${recap.HCCDescription}`)
                                        }
                                      >
                                        View Evidence
                                      </Button>
                                      {recap.Feedbacks?.length > 0 && (
                                        <Button
                                          variant="contained"
                                          size="small"
                                          sx={{
                                            backgroundColor: '#ADD8E6',
                                            color: '#000',
                                            fontSize: '12px',
                                            padding: '4px 8px',
                                            '&:hover': { backgroundColor: '#BFEFFF' },
                                          }}
                                          onClick={() => handleViewNotes(recap.Feedbacks)}
                                        >
                                          Notes
                                        </Button>
                                      )}
                                      <Button
                                        variant="contained"
                                        size="small"
                                        sx={{
                                          backgroundColor: '#ADD8E6',
                                          color: '#000',
                                          fontSize: '12px',
                                          padding: '4px 8px',
                                          '&:hover': { backgroundColor: '#BFEFFF' },
                                        }}
                                        onClick={(event) => handleAckRecapture(event, recap.MessageId, recap.ModuleName)}
                                      >
                                        Resolve
                                      </Button>
                                    </Box>
                                  </TableCell>
                                </TableRow>
                              </React.Fragment>
                            );
                        } else if (recap.ModuleName === "Clinical_Leadership") {
                          return (<TableRow key={recap.MessageId}>
                            <TableCell sx={{ padding: '4px', bgcolor: "#fdedec" }}>
                              <strong>{recap.CreatedBy}</strong>: {recap.Description}
                            </TableCell>
                            <TableCell sx={{ padding: '4px', bgcolor: "#fdedec" }} align="right">
                              <FormControl variant="standard" sx={{ m: 1, minWidth: 46 }}>
                                {recap.Feedbacks?.length > 0 && (
                                  <Button
                                    variant="contained"
                                    size="small"
                                    sx={{
                                      backgroundColor: '#BBBBBB',
                                      color: '#000',
                                      fontSize: '12px',
                                      padding: '4px 8px',
                                      '&:hover': { backgroundColor: '#BFEFFF' },
                                      marginBottom: '5px'
                                    }}
                                    onClick={() => handleViewNotes(recap.Feedbacks)}
                                  >
                                    Notes
                                  </Button>
                                )}
                                <Button
                                  variant="contained"
                                  size="small"
                                  sx={{
                                    backgroundColor: '#ADD8E6',
                                    color: '#000',
                                    fontSize: '12px',
                                    padding: '4px 8px',
                                    '&:hover': { backgroundColor: '#BFEFFF' },
                                  }}
                                  onClick={(event) => handleAckCoding(event, recap.MessageId, recap.ModuleName)}
                                >
                                  Resolve
                                </Button>
                              </FormControl>
                            </TableCell>
                          </TableRow>);
                        }
                        return null; // If no matching ModuleName, return null  
                      })
                      }
                      </React.Fragment>)
                    )}

                </TableBody>
            </Table>
        </TableContainer>
        <Divider sx={{ height:10 }}/></div>)
    } 

    if (patient.ActiveCoding === 1) {
      codingComponent = (<div><Box
          display="flex"
          alignItems="center"
          justifyContent="space-between"
          bgcolor="teal"
          color="white"
          p={1}
          borderRadius="4px 4px 0 0"
      >
          Coder Suggested Actions
      </Box>

      <TableContainer component={Paper}>
        <Table aria-label="simple table">
          <TableBody>
            {hccCoding.map((dx, index) => (
              <React.Fragment key={`dx-${index}`}>
                {/* Spacer Row */}
                <TableRow>
                  <TableCell colSpan={2} sx={{ padding: '3px', bgcolor: 'darkgray' }} />
                </TableRow>

                {/* Header Row — assuming the first gap represents the group */}
                {dx.length > 0 && (
                  <TableRow>
                    <TableCell colSpan={2} sx={{ padding: '8px', bgcolor: 'lightgray' }}>
                      <Typography component="span" sx={{ fontWeight: 'bold' }}>
                        {dx[0].DiagnosisCode} - {dx[0].DiagnosisDescription} (HCC: {dx[0].HCC?.split(' - ')[0]})
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}

                {/* Gap Rows */}
                {dx.map(gap => (
                  <TableRow key={gap.MessageId}>
                    <TableCell sx={{ padding: '4px', bgcolor: "#fdedec" }}>
                      <strong>{gap.CreatedBy}</strong>: {gap.Description}
                    </TableCell>
                    <TableCell sx={{ padding: '4px', bgcolor: "#fdedec" }} align="right">
                      <FormControl variant="standard" sx={{ m: 1, minWidth: 46 }}>
                        {gap.Feedbacks?.length > 0 && (
                          <Button
                            variant="contained"
                            size="small"
                            sx={{
                              backgroundColor: '#BBBBBB',
                              color: '#000',
                              fontSize: '12px',
                              padding: '4px 8px',
                              '&:hover': { backgroundColor: '#BFEFFF' },
                              marginBottom: '5px'
                            }}
                            onClick={() => handleViewNotes(gap.Feedbacks)}
                          >
                            Notes
                          </Button>
                        )}
                        <Button
                          variant="contained"
                          size="small"
                          sx={{
                            backgroundColor: '#ADD8E6',
                            color: '#000',
                            fontSize: '12px',
                            padding: '4px 8px',
                            '&:hover': { backgroundColor: '#BFEFFF' },
                          }}
                          onClick={(event) => handleAckCoding(event, gap.MessageId, gap.ModuleName)}
                        >
                          Resolve
                        </Button>
                      </FormControl>
                    </TableCell>
                  </TableRow>
                ))}
              </React.Fragment>
            ))}
          </TableBody>
        </Table>
      </TableContainer>


      <Divider sx={{ height:10 }}/></div>)
  } 

    if (patient.ActiveRec === 1) {
        hasInfo = 1
        recCompoent = (<div>
            <Box
              display="flex"
              alignItems="center"
              justifyContent="space-between"
              bgcolor="teal"
              color="white"
              p={1}
              borderRadius="4px 4px 0 0"
            >
              HCC Recommendations
            </Box>
            <TableContainer component={Paper}>
              <Table aria-label="simple table">
                <TableBody>
                  {hccRecommendations.map((rec, index) => (
                    <React.Fragment key={rec.MessageId}>
                      <TableRow sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                        <TableCell sx={{ padding: '3px', bgcolor: 'darkgray' }} component="th" scope="row"></TableCell>
                      </TableRow>
                      <TableRow sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                        <TableCell sx={{ padding: '8px', bgcolor: 'lightgray' }} component="th" scope="row">
                        <Typography component="span" sx={{ fontWeight: 'bold' }}>
                            {rec.DiagnosisCode} (HCC {rec.HCC}):
                        </Typography> {rec.Description}
                        </TableCell>
                      </TableRow>
                      <TableRow sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                        <TableCell sx={{ padding: '8px', bgcolor: "#ff9d9c" }} align="left">
                          <Box display="flex" alignItems="center">
                            <Box sx={{ flexGrow: 1 }}>
                              {rec.Reason}
                            </Box>
                            <FormControl variant="standard" sx={{ m: 0, minWidth: 46 }}>
                                  <Checkbox
                                    sx={{ '& .MuiSvgIcon-root': { fontSize: 28 } }}
                                    onChange={(event) => handleAckRecommendations(event, rec.MessageId)}
                                  />
                                </FormControl>
                          </Box>
                        </TableCell>
                      </TableRow>
                    </React.Fragment>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </div>)
    }

    if (patient.ActiveAllymar === 1 && patient.Allymar && patient.Allymar.length > 0) {
      allymarComponent = (
        <div>
          <Box
            display="flex"
            alignItems="center"
            justifyContent="space-between"
            bgcolor="teal"
            color="white"
            p={1}
            borderRadius="4px 4px 0 0"
          >
            Allymar Detections
          </Box>
          <TableContainer component={Paper}>
            <Table aria-label="allymar-table">
              <TableBody>
                {patient.Allymar.map((group, idx) =>
                  group.map((item) => (
                    <React.Fragment key={item.MessageId}>
                      {/* Spacer Row */}
                      <TableRow>
                        <TableCell colSpan={2} sx={{ padding: '3px', bgcolor: 'darkgray' }} />
                      </TableRow>
                      {/* Header Row */}
                      <TableRow>
                        <TableCell colSpan={2} sx={{ padding: '8px', bgcolor: 'lightgray' }}>
                          <Typography component="span" sx={{ fontWeight: 'bold' }}>
                            {item.DiagnosisCode} - {item.DiagnosisDescription} (HCC {item.HccCode})
                          </Typography>
                          <br />
                          <span style={{ fontSize: '0.8em', color: '#555555' }}>
                            Allymar Version: {item.AllymarDate}
                          </span>
                        </TableCell>
                      </TableRow>
                      {/* Details Row */}
                      <TableRow>
                        <TableCell colSpan={2} sx={{ padding: '8px', bgcolor: '#fdedec' }}>
                          <Box>
                            {item.Notes && item.Notes !== "" && (
                              <Box mb={1}>
                                <Typography variant="body2" sx={{ fontWeight: 'bold' }}>Notes:</Typography>
                                <Typography variant="body2" component="span">
                                  {expandedAllymarNotes[item.MessageId] || item.Notes.length <= 100
                                    ? item.Notes
                                    : (
                                        <>
                                          {item.Notes.slice(0, 200)}...
                                          <span
                                            style={{ color: '#1976d2', cursor: 'pointer', textDecoration: 'underline', marginLeft: 4 }}
                                            onClick={() => handleToggleAllymarNote(item.MessageId)}
                                          >
                                            [ more ]
                                          </span>
                                        </>
                                      )
                                  }
                                  {item.Notes.length > 200 && expandedAllymarNotes[item.MessageId] && (
                                    <span
                                      style={{ color: '#1976d2', cursor: 'pointer', textDecoration: 'underline', marginLeft: 4 }}
                                      onClick={() => handleToggleAllymarNote(item.MessageId)}
                                    >
                                      [ less ]
                                    </span>
                                  )}
                                </Typography>
                              </Box>
                            )}
                            {item.Symptoms && item.Symptoms.length > 0 && (
                              <Box mb={1}>
                                <Typography variant="body2" sx={{ fontWeight: 'bold' }}>Symptoms:</Typography>
                                <Typography variant="body2">{item.Symptoms.join(', ')}</Typography>
                              </Box>
                            )}
                            {item.Medications && item.Medications.length > 0 && (
                              <Box mb={1}>
                                <Typography variant="body2" sx={{ fontWeight: 'bold' }}>Medications:</Typography>
                                <Typography variant="body2">{item.Medications.join(', ')}</Typography>
                              </Box>
                            )}
                            {item.Labs && item.Labs.length > 0 && (
                              <Box mb={1}>
                                <Typography variant="body2" sx={{ fontWeight: 'bold' }}>Labs:</Typography>
                                <Typography variant="body2">{item.Labs.join(', ')}</Typography>
                              </Box>
                            )}
                            {item.ChartToPages && item.ChartToPages.length > 0 && (
                              <Box mb={1}>
                                <Typography variant="body2" sx={{ fontWeight: 'bold' }}>Chart Pages:</Typography>
                                <ul style={{ margin: 0, paddingLeft: 20 }}>
                                  {item.ChartToPages.map((ct, i) => (
                                    <li key={i}>
                                      <span
                                        style={{ fontWeight: 'bold', color: '#1976d2', cursor: 'pointer', textDecoration: 'underline' }}
                                        onClick={() => handleOpenPdf(ct.filename)}
                                      >
                                        {ct.filename}
                                      </span>
                                      : pages {ct.pages.join(', ')}
                                    </li>
                                  ))}
                                </ul>
                              </Box>
                            )}
                          </Box>
                        </TableCell>
                      </TableRow>
                      <TableRow>
                      <TableCell sx={{ padding: '8px', bgcolor: '#fdedec' }} align="right">
                        <Box display="flex" justifyContent="flex-end" gap={1}>
                          {item.Feedbacks && item.Feedbacks.length > 0 && (
                            <Button
                              variant="contained"
                              size="small"
                              sx={{
                                backgroundColor: '#BBBBBB',
                                color: '#000',
                                fontSize: '12px',
                                padding: '4px 8px',
                                '&:hover': { backgroundColor: '#BFEFFF' },
                              }}
                              onClick={() => handleViewNotes(item.Feedbacks)}
                            >
                              Notes
                            </Button>
                          )}
                          <Button
                            variant="contained"
                            size="small"
                            sx={{
                              backgroundColor: '#ADD8E6',
                              color: '#000',
                              fontSize: '12px',
                              padding: '4px 8px',
                              '&:hover': { backgroundColor: '#BFEFFF' },
                            }}
                            onClick={(event) => handleAckCoding(event, item.MessageId, item.ModuleName)}
                          >
                            Resolve
                          </Button>
                        </Box>
                      </TableCell>
                      </TableRow>
                    </React.Fragment>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <Divider sx={{ height: 10 }} />
        </div>
      );
    }

    return (
        <div>
            <Accordion defaultExpanded>
                <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                sx={{ backgroundColor: '#d5dbdb', margin: '0 !important', minHeight: 'unset !important', '& .MuiAccordionSummary-content': { margin: '5px 0 !important' } }}
                >
                    <Typography variant="h6">Disease Detection</Typography>
                </AccordionSummary>
                <AccordionDetails sx={{ backgroundColor: '#eaeded', padding: '12px' }}>
                    {gapComponent}
                    {recCompoent}
                    {codingComponent}
                    {allymarComponent}
                </AccordionDetails>
            </Accordion>
            <Divider sx={{ height:15 }}/>
            <Feedback open={isFeedbackOpen} onClose={handleCloseFeedback} patientId={patientId} username={username} token={token} module={feedbackConfig} />
            <Dialog open={openNotesModal} onClose={handleCloseNotesModal} maxWidth="sm" fullWidth>
              <DialogTitle>Follow Up Note History</DialogTitle>
              <DialogContent>
                  <Table>
                      <TableBody>
                          {activeFeedbacks.map((fb, index) => (
                              <TableRow key={index}>
                                  <TableCell>{fb.feedback}<br /><span style={{ fontSize: '0.8em', color: '#555555' }}>By: {fb.feedback_by} on {fb.feedback_at}</span></TableCell>
                              </TableRow>
                          ))}
                      </TableBody>
                  </Table>
              </DialogContent>
              <DialogActions>
                  <Button onClick={handleCloseNotesModal}>Close</Button>
              </DialogActions>
            </Dialog>
            <Dialog open={openEvidenceModal} onClose={handleCloseEvidenceModal} maxWidth="md" fullWidth>
              <DialogTitle>Evidence for {activeDiagnosisHeader}</DialogTitle>
              <DialogContent dividers>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Detail</TableCell>
                      <TableCell>Code</TableCell>
                      <TableCell>Date</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {activeEvidences.map((ev, idx) => (
                      <TableRow key={idx}>
                        <TableCell>{ev.detail || '(No detail)'}</TableCell>
                        <TableCell>{ev.diagnosis_code}</TableCell>
                        <TableCell>{ev.detected_date}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </DialogContent>
              <DialogActions>
                <Button onClick={handleCloseEvidenceModal}>Close</Button>
              </DialogActions>
            </Dialog>
            <Dialog open={loadingFile}>
              <DialogTitle>Loading File</DialogTitle>
              <DialogContent>
                <Typography>Please wait while the PDF is loading...</Typography>
              </DialogContent>
            </Dialog>
        </div>
    );
}

export default TealDiseaseDetection;
